/**
 * Settings page parts (DESIGN.md T5): a card per topic (optionally collapsible, remembering its
 * open state), the lock badge for values owned by Ansible (layer A, read-only here) and the
 * sticky footer with the one Save of the page.
 */
import { ChevronRightIcon, LockIcon } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { Badge } from '../components/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';
import { useRememberedOpen } from '../hooks/use-remembered-open';
import { cn } from '../lib/utils';

/** "Managed by Ansible" badge; the tooltip explains where the value is set. */
export function LockBadge({ label, hint }: { label: string; hint?: string }) {
  const badge = (
    <Badge variant="muted" tabIndex={hint ? 0 : undefined}>
      <LockIcon />
      {label}
    </Badge>
  );
  if (!hint) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

export interface SettingsCardProps {
  title: string;
  description?: string;
  /** Badges next to the title (e.g. LockBadge). */
  status?: ReactNode;
  /** Buttons at the top right of the card. */
  actions?: ReactNode;
  className?: string;
  /**
   * Makes the card a `CollapsibleSection`: the id remembers its open state per page
   * (`<page>.<card>`, e.g. `branding.images`).
   */
  collapseId?: string;
  /** One-line summary shown while collapsed (current value, count). */
  summary?: ReactNode;
  /** Open state before the user chose one. Default true. */
  defaultOpen?: boolean;
  children: ReactNode;
}

export function SettingsCard({ collapseId, ...props }: SettingsCardProps) {
  if (collapseId) return <CollapsibleSection id={collapseId} {...props} />;
  const { title, description, status, actions, className, children } = props;
  return (
    <Card className={className}>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{title}</CardTitle>
            {status}
          </div>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </CardHeader>
      <CardContent className="flex flex-col gap-section">{children}</CardContent>
    </Card>
  );
}

export interface CollapsibleSectionProps extends Omit<SettingsCardProps, 'collapseId'> {
  /** Remembers the open state (per browser) under this id, e.g. `journaling.rules`. */
  id: string;
}

/**
 * Settings card that folds to its header (DESIGN.md T5 "collapsible sections"): long settings
 * pages show topics as one-line summaries and open what the admin works on. The content stays
 * mounted while folded, so forms keep their values and validation.
 */
export function CollapsibleSection({ id, title, description, status, actions, className, summary, defaultOpen = true, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useRememberedOpen(id, defaultOpen);
  const contentId = useId();
  return (
    <Card className={cn(!open && 'gap-0 self-start', className)} data-state={open ? 'open' : 'closed'}>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={contentId}
              onClick={() => setOpen(!open)}
              className="-ml-1 flex items-center gap-1 rounded-sm px-1 text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              <ChevronRightIcon aria-hidden className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
              <CardTitle>{title}</CardTitle>
            </button>
            {status}
            {!open && summary && <span className="min-w-0 truncate text-xs text-muted-foreground">{summary}</span>}
          </div>
          {open && description && <CardDescription>{description}</CardDescription>}
        </div>
        {open && actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </CardHeader>
      <CardContent id={contentId} hidden={!open} className="flex flex-col gap-section">
        {children}
      </CardContent>
    </Card>
  );
}

/** Sticky footer of a settings page: [Save] [Reset] left, notes right. */
export function SettingsFooter({ children, end, className }: { children: ReactNode; end?: ReactNode; className?: string }) {
  return (
    <div className={cn('sticky bottom-0 z-10 -mx-page flex flex-wrap items-center justify-between gap-2 border-t bg-background/95 px-page py-2 backdrop-blur', className)}>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {end && <div className="text-sm text-muted-foreground">{end}</div>}
    </div>
  );
}
